import React, { useState, useEffect } from 'react';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Slider } from 'primereact/slider';
import { ProductBatchExpireMetadata, ProductExpiryStatusFilterDTO } from "../../models/productbatch-model";
import styles from './styles/productbatchexpire-search.module.css';

interface ProductBatchExpireSearchProps {
  metadata: ProductBatchExpireMetadata | null;
  onSearch: (filters: ProductExpiryStatusFilterDTO) => void;
  isLoading?: boolean;
}

interface PosOption {
  id: number;
  name: string;
}

interface CategoryOption {
  id: number;
  name: string;
}

interface ProductOption {
  id: number;
  name: string;
}

const ProductBatchExpireSearch: React.FC<ProductBatchExpireSearchProps> = ({
  metadata,
  onSearch,
  isLoading = false,
}) => {
  const [filters, setFilters] = useState<ProductExpiryStatusFilterDTO>({
    posId: null,
    productId: null,
    categoryId: null,
    batchNo: null,
    expiryDateFrom: null,
    expiryDateTo: null,
    expiresInDaysFrom: null,
    expiresInDaysTo: null,
    correntStockFlag: 0,
    pageNumber: 1,
    pageSize: 10,
    sortColumn: null,
    sortDirection: null,
  });

  const [expiresInDaysRange, setExpiresInDaysRange] = useState<[number, number]>([-365, 365]);
  const [viewAllBatches, setViewAllBatches] = useState(false);
  
  // Dropdown options state
  const [posOptions, setPosOptions] = useState<PosOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);

  // Initialize dropdown options
  useEffect(() => {
    if (metadata) {
      setPosOptions(metadata.pos || []);
      setCategoryOptions(metadata.categories || []);
      setProductOptions(metadata.products || []);
    }
  }, [metadata]);

  const handlePosChange = (e: DropdownChangeEvent) => {
    setFilters({
      ...filters,
      posId: e.value,
      sortColumn: null,
      sortDirection: null,
    });
  };

  const handleProductChange = (e: DropdownChangeEvent) => {
    setFilters({
      ...filters,
      productId: e.value,
      sortColumn: null,
      sortDirection: null,
    });
  };

  const handleCategoryChange = (e: DropdownChangeEvent) => {
    setFilters({
      ...filters,
      categoryId: e.value,
      sortColumn: null,
      sortDirection: null,
    });
  };

  // const handleCategoryFilter = (e: DropdownFilterEvent) => {
  //   if (e.filter && e.filter.length > 0) {
  //     const filtered = (metadata?.categories || []).filter((cat) =>
  //       cat.name.toLowerCase().includes(e.filter.toLowerCase())
  //     );
  //     setCategoryOptions(filtered);
  //   } else {
  //     setCategoryOptions(metadata?.categories || []);
  //   }
  // };

  // const handleProductFilter = (e: DropdownFilterEvent) => {
  //   if (e.filter && e.filter.length > 0) {
  //     const filtered = (metadata?.products || []).filter((prod) =>
  //       prod.name.toLowerCase().includes(e.filter.toLowerCase())
  //     );
  //     setProductOptions(filtered);
  //   } else {
  //     setProductOptions(metadata?.products || []);
  //   }
  // };

  const handleBatchNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     const value = e.target.value.trim();
    setFilters({
      ...filters,
      batchNo: value.length > 0 ? value : null,
      sortColumn: null,
      sortDirection: null,
    });
  };

  const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

  const handleExpiryDateFromChange = (e: any) => {
    const date = e.value ? formatDate(e.value) : null;
    setFilters({
      ...filters,
      expiryDateFrom: date,
      sortColumn: null,
      sortDirection: null,
    });
  };

  const handleExpiryDateToChange = (e: any) => {
    const date = e.value ? formatDate(e.value) : null;
    setFilters({
      ...filters,
      expiryDateTo: date,
      sortColumn: null,
      sortDirection: null,
    });
  };

  const handleRangeChange = (e: any) => {
    setExpiresInDaysRange(e.value);
    setFilters({
      ...filters,
      expiresInDaysFrom: e.value[0],
      expiresInDaysTo: e.value[1],
      sortColumn: null,
      sortDirection: null,
    });
  };

  const handleViewAllBatchesChange = (e: any) => {
    setViewAllBatches(e.checked);
    setFilters({
      ...filters,
      correntStockFlag: e.checked ? 1 : 0, 
      sortColumn: null,
      sortDirection: null,
    });
  };

  const handleSearch = () => {
    const searchFilters = {
      ...filters,
      pageNumber: 1,
      sortColumn: null,
      sortDirection: null,
    };
    onSearch(searchFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      posId: null,
      productId: null,
      categoryId: null,
      batchNo: null,
      expiryDateFrom: null,
      expiryDateTo: null,
      expiresInDaysFrom: null,
      expiresInDaysTo: null,
      correntStockFlag: 0,
      pageNumber: 1,
      pageSize: 10,
      sortColumn: null,
      sortDirection: null,
    };
    setFilters(resetFilters);
    setExpiresInDaysRange([-365, 365]);
    setViewAllBatches(false);
    // Reset dropdown options
    setPosOptions(metadata?.pos || []);
    setCategoryOptions(metadata?.categories || []);
    setProductOptions(metadata?.products || []);
    // Trigger search with reset filters
    onSearch(resetFilters);
  };

  return (
    <div className={styles.expirySearchContainer}>
      <div className={styles.expirySearchPanelHeader}>
        <i className="pi pi-filter-fill"></i>
        <span>Search & Filter</span>
      </div>

      <div className={styles.expiryFilterSection}>
        {/* POS Dropdown */}
        <div>
          <label className={styles.expiryFilterLabel}>POS</label>
          <Dropdown
            value={filters.posId}
            onChange={handlePosChange}
            options={posOptions}
            optionLabel="name"
            optionValue="id"
            placeholder="Select POS"
            panelClassName="product-batch-expire-dropdown-panel"
            showClear
          />
        </div>

        {/* Category Dropdown with Filter */}
        <div>
          <label className={styles.expiryFilterLabel}>Category</label>
          <Dropdown
            value={filters.categoryId}
            onChange={handleCategoryChange}
            // onFilter={handleCategoryFilter}
            options={metadata?.categories || []}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Category"
            panelClassName="product-batch-expire-dropdown-panel"
            filter
            showClear
          />
        </div>

        {/* Product Dropdown with Filter */}
        <div>
          <label className={styles.expiryFilterLabel}>Product</label>
          <Dropdown
            value={filters.productId}
            onChange={handleProductChange}
            // onFilter={handleProductFilter}
            options={metadata?.products || []}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Product"
            panelClassName="product-batch-expire-dropdown-panel"
            filter
            showClear
          />
        </div>

        {/* Batch No Input */}
        <div>
          <label className={styles.expiryFilterLabel}>Batch No</label>
          <InputText
            value={filters.batchNo || ''}
            onChange={handleBatchNoChange}
            placeholder="Enter Batch No"
            className="w-full"
          />
        </div>

        {/* Expiry Date From */}
        <div>
          <label className={styles.expiryFilterLabel}>Expiry Date From</label>
          <Calendar
            value={filters.expiryDateFrom ? new Date(filters.expiryDateFrom) : null}
            onChange={handleExpiryDateFromChange}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="From"
            panelClassName="expiry-search-datepicker"
          />
        </div>

        {/* Expiry Date To */}
        <div>
          <label className={styles.expiryFilterLabel}>Expiry Date To</label>
          <Calendar
            value={filters.expiryDateTo ? new Date(filters.expiryDateTo) : null}
            onChange={handleExpiryDateToChange}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="To"
            panelClassName="expiry-search-datepicker"
          />
        </div>

        {/* Expires In Days Range Slider */}
        <div className={styles.expiryRangeSliderWrapper}>
          <label className={styles.expiryRangeSliderLabel}>
            Expires In Days: {expiresInDaysRange[0]} to {expiresInDaysRange[1]} days
          </label>
          <div className={styles.expirySliderContainer}>
            <span className={styles.expirySliderLabel}>-365</span>
            <Slider
              value={expiresInDaysRange}
              onChange={handleRangeChange}
              range
              min={-365}
              max={365}
              step={1}
              className={styles.expiryRangeSlider}
            />
            <span className={styles.expirySliderLabel}>+365</span>
          </div>
        </div>
      </div>

      {/* View All Batches Checkbox */}
      <div className={styles.expiryCheckboxGroup}>
        <Checkbox
          inputId="viewAllBatches"
          checked={viewAllBatches}
          onChange={handleViewAllBatchesChange}
        />
        <label htmlFor="viewAllBatches" className={styles.expiryCheckboxLabel}>
          View All Batches (Including Empty Stock)
        </label>
      </div>

      {/* Action Buttons */}
      <div className={styles.expiryButtonGroup}>
        <Button
          label="Filter"
          icon="pi pi-filter"
          onClick={handleSearch}
          className={styles.searchButtonPrimary}
          loading={isLoading}
        />
        <Button
          label="Reset"
          icon="pi pi-refresh"
          onClick={handleReset}
          className={styles.searchButtonSecondary}
        />
      </div>
    </div>
  );
};

export default ProductBatchExpireSearch;
